// Add polyfill for global object
if (typeof window !== 'undefined' && window.global === undefined) {
  window.global = window;
}

// Enhanced error handling for dynamic module loading failures
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason?.message || '';
  
  // Handle dynamic import loading errors
  if (message.includes('Loading chunk') || 
      message.includes('Loading CSS chunk') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('error loading dynamically imported module') ||
      message.includes('ChunkLoadError')) {
    console.error('Dynamic module loading failed:', message);
    
    // Attempt to reload the page once as a recovery mechanism
    if (!window.chunkLoadRetryAttempted) {
      window.chunkLoadRetryAttempted = true;
      console.warn('Attempting page reload to recover from chunk loading failure...');
      
      // Clear caches before reload to ensure fresh assets
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => registration.unregister());
        });
      }
      
      // Clear browser caches
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      
      setTimeout(() => {
        window.location.reload(true); // Force reload bypassing cache
      }, 1000);
    }
    
    event.preventDefault();
    return;
  }
  
  // Handle other module script errors
  if (message.includes('Unable to preload CSS') || 
      message.includes('Importing a module script failed')) {
    console.warn('Module/CSS loading issue:', message);
    event.preventDefault();
    return;
  }
  
  // Log other unhandled rejections for debugging
  console.error('Unhandled promise rejection:', reason);
});

// Handle general JavaScript errors including chunk load errors
window.addEventListener('error', (event) => {
  const message = event.message || '';
  const source = event.filename || '';
  
  // Handle chunk loading errors from script tags
  if (source.includes('assets/') && (
      message.includes('Loading chunk') ||
      message.includes('Script error') ||
      event.target?.tagName === 'SCRIPT'
    )) {
    console.error('Script/Chunk loading error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
    
    // Retry script loading once
    if (event.target?.tagName === 'SCRIPT' && !event.target.retried) {
      event.target.retried = true;
      const newScript = document.createElement('script');
      newScript.src = event.target.src;
      newScript.type = event.target.type || 'text/javascript';
      document.head.appendChild(newScript);
      console.warn('Retrying script load:', event.target.src);
    }
  }
});

// Handle CSS preloading errors in Safari
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('Unable to preload CSS')) {
    console.warn('CSS preloading failed, continuing without preload:', event.reason.message);
    event.preventDefault(); // Prevent the error from being logged as unhandled
  }
});

import "primeicons/primeicons.css";
import "./assets/main.css";
import "./styles/place-autocomplete.css";

import { createApp } from "vue";
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import App from "./App.vue";
import "./assets/flags.css";
import Button from "primevue/button";
import CheckBox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import InputNumber from 'primevue/inputnumber';
import DatePicker from 'primevue/datepicker';
import Dropdown from 'primevue/dropdown';
import FloatLabel from "primevue/floatlabel";
import Popover from "primevue/popover";
import router from "../router.js"; // Import the router
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import "primeflex/primeflex.css"; // Add this import
// import { Form } from "@primevue/forms";
import { useI18n } from "./boot/i18n"; // Import the useI18n function
import * as Sentry from "@sentry/vue";
import config from '../env.config.json'; // Adjust the path if needed
import Select from 'primevue/select';
import Divider from 'primevue/divider';
import ProgressSpinner from 'primevue/progressspinner';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import VuePhoneNumberInput, { RETURN } from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import HotjarPlugin from './boot/hotjar';
import { storage } from './utils/storage';
// Get the i18n instance
const { i18n } = useI18n(); 
const environment = process.env.NODE_ENV || 'localhost'; // Default to 'localhost'
const apiConfig = config[environment];

// Log the API URL for debugging
 

// Create the Vue app
const app = createApp(App);

// Set global properties for API configuration
app.config.globalProperties.$apiConfig = apiConfig;

// Use PrimeVue with the Aura theme - explicitly disable dark mode
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
    },
  },
});

// Initialize Hotjar
app.use(HotjarPlugin);

// Initialize Sentry - Skip for localhost environment
if (environment !== 'localhost') {
  Sentry.init({
    app,
    dsn: "https://0ebb7ba9fef3471bbaa531795981589b@o4504873513385984.ingest.us.sentry.io/4504873560702976",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      "localhost:8000",
      "localhost:8080", 
      /^https:\/\/api\.dev\.gosharpei\.com/,
      /^https:\/\/api\.prod\.gosharpei\.com/,
      /^https:\/\/terrace\.finance/
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Register components
app.component("Button", Button);
app.component("InputText", InputText);
app.component("FloatLabel", FloatLabel);
app.component("CheckBox", CheckBox);
app.component("Popover", Popover);
// app.component("Form", Form);
app.component("Toast", Toast);
app.component('InputNumber', InputNumber);
app.component('DatePicker', DatePicker);
app.component('Dropdown', Dropdown);
app.component('Select', Select);
app.component('Divider', Divider);
app.component('vue-phone-number-input', VuePhoneNumberInput);
app.component('ProgressSpinner', ProgressSpinner);
app.component('Stepper', Stepper);
app.component('StepList', StepList);
app.component('StepPanels', StepPanels);
app.component('StepItem', StepItem);
app.component('Step', Step);
app.component('StepPanel', StepPanel);

// Use the router and i18n
app.use(router);
app.use(i18n);
app.use(ToastService); // Register the ToastService

// Mount the Vue app
app.mount("#app");

// Register directives
app.directive('tooltip', Tooltip);

// Session management - runs when the app loads
(() => {
  // First, migrate any existing localStorage data to sessionStorage
  storage.migrateCheckoutData();
  
  // Get current URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('s');
  const storedSessionId = storage.session.get('session_id');
  
  // Only process if there's a session ID in the URL
  if (sessionId) {
    // If stored session ID is different from URL session ID
    if (storedSessionId !== sessionId) {
      // Clear checkout data first (this also resets grenke_state isCartLimitExceed flag)
      storage.clearCheckoutData();
      // Set the new session ID in sessionStorage
      storage.session.set('session_id', sessionId);
      // Prevent infinite reload by checking if we just reloaded
      if (!storage.local.get('just_reloaded')) {
        storage.local.set('just_reloaded', true);
        window.location.reload();
        return; // Exit early as we're reloading
      }
    }
  }
  
  // Clear the reload flag if it exists
  storage.local.remove('just_reloaded');
})();
